import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@tencent/dui/lib/components/Button';
import i18n from '@i18next/i18n';
import { fullDateFormat } from '@editor/src/js/utils/timeUtils';
import styles from './snapshot-list.less';
import type { SnapshotDataItemsModel, SnapshotMetaItemsModel, SnapshotStorage } from '@tencent/slide-sdk-persistent/src/snapshot-manager';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

interface ISnapshotListProps {
    visible: boolean;
    storage?: SnapshotStorage;
    style?: React.CSSProperties;
    exportSnapshotFile?(snapshot: SnapshotDataItemsModel): Promise<void>;
}

export function SnapshotList({ visible = false, style = {}, exportSnapshotFile,
    storage: propsStorage }: ISnapshotListProps) {
    const [snapshots, setSnapshots] = useState<SnapshotMetaItemsModel[]>([]);

    useEffect(() => {
        const storage = propsStorage ?? SlideDataServices.SDKDataServices.SnapshotManager()
            ?.getSnapshotStorage();
        if (!visible || !storage) return;

        storage
            .list()
            .then(snapshots => setSnapshots(snapshots.reverse()))
            .catch((e) => {
                window.log.error('[SnapshotList] get snapshot from store failed', e);
            });
    }, [visible]);

    if (!visible) return null;

    return (
        <ul className={styles.list} style={style}>
            {snapshots.length ? snapshots.map(s => (
                <li className={styles.item} key={s.id}>
                    <div className={styles.time}>
                        {fullDateFormat(new Date(s.createdAt))}
                    </div>
                    <div className={styles.actions}>
                        <Button className={styles.action} size="small" onClick={async () => {
                            const storage = propsStorage ?? SlideDataServices.SDKDataServices.SnapshotManager()
                                ?.getSnapshotStorage();
                            const snapshot = await storage?.getById(s.id);
                            snapshot && exportSnapshotFile?.(snapshot);
                        }}>
                            {i18n.t('导出为')} .pptx
                        </Button>
                    </div>
                </li>
            )) : i18n.t('无数据')}
        </ul>
    );
}

SnapshotList.propTypes = {
    visible: PropTypes.bool,
    style: PropTypes.object,
};

interface ISnapshotListFromServerProps {
    visible: boolean;
    style?: React.CSSProperties;
    exportSnapshotFile?(): void;
}

export function SnapshotListFromServer({
    visible = false, style = {}, exportSnapshotFile,
}: ISnapshotListFromServerProps) {
    let lastModifyTimeFromSlideData = SlideDataServices.DocInfoManager()?.docInfo?.doc_meta?.last_modify_ts;
    lastModifyTimeFromSlideData = lastModifyTimeFromSlideData ? lastModifyTimeFromSlideData * 1000 : undefined;
    let { lastModifyTime } = window.basicClientVars?.docInfo ?? {};
    lastModifyTime = lastModifyTime ?? lastModifyTimeFromSlideData ?? Date.now();
    if (!visible) return null;
    return (
        <ul className={styles.list} style={style}>
            <li className={styles.item}>
                <div className={styles.time}>
                    {fullDateFormat(new Date(lastModifyTime))}
                </div>
                <div className={styles.actions}>
                    <Button className={styles.action} size="small" onClick={exportSnapshotFile}>
                        {i18n.t('导出为')} .pptx
                    </Button>
                </div>
            </li>
        </ul>
    );
}

function createContinuousClickHandler(cb, threshold = 3) {
    let counter = 0;
    let last = Date.now();

    return () => {
        const now = Date.now();
        if (now - last < 250) {
            counter += 1;
        } else {
            counter = 1;
        }

        if (counter >= threshold) {
            cb();
            counter = 0;
        }

        last = Date.now();
    };
}

export { createContinuousClickHandler };
